import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Authorization, Member } from '../types';
import {
  FileCheck,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Sparkles,
  AlertCircle,
} from 'lucide-react';

interface AuthWithMember extends Authorization {
  member?: Member;
}

export default function Authorizations() {
  const [authorizations, setAuthorizations] = useState<AuthWithMember[]>([]);
  const [selectedAuth, setSelectedAuth] = useState<AuthWithMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [decisionRationale, setDecisionRationale] = useState('');

  useEffect(() => {
    fetchAuthorizations();
  }, []);

  const fetchAuthorizations = async () => {
    try {
      const [authsResult, membersResult] = await Promise.all([
        supabase
          .from('authorizations')
          .select('*')
          .order('requested_date', { ascending: false }),
        supabase.from('members').select('*'),
      ]);

      if (authsResult.data && membersResult.data) {
        const authsWithMembers = authsResult.data.map((auth) => ({
          ...auth,
          member: membersResult.data.find((m) => m.id === auth.member_id),
        }));
        setAuthorizations(authsWithMembers);
        if (authsWithMembers.length > 0) {
          setSelectedAuth(authsWithMembers[0]);
          setDecisionRationale(authsWithMembers[0].decision_rationale || '');
        }
      }
    } catch (error) {
      console.error('Error fetching authorizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Denied':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Expired':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Emergent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Urgent':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const calculateSLA = (requestedDate: string, slaHours: number) => {
    const requested = new Date(requestedDate);
    const deadline = new Date(requested.getTime() + slaHours * 60 * 60 * 1000);
    const now = new Date();
    const remaining = deadline.getTime() - now.getTime();
    const hoursRemaining = Math.floor(remaining / (1000 * 60 * 60));
    const daysRemaining = Math.floor(hoursRemaining / 24);

    if (remaining < 0) {
      return { text: 'Overdue', color: 'text-red-600', overdue: true };
    } else if (hoursRemaining < 24) {
      return {
        text: `${hoursRemaining}h remaining`,
        color: 'text-orange-600',
        overdue: false,
      };
    } else {
      return {
        text: `${daysRemaining}d remaining`,
        color: 'text-green-600',
        overdue: false,
      };
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading authorizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full space-x-6">
      {/* Left Panel - Authorization Queue */}
      <div className="w-96 bg-white rounded-lg border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Authorization Queue</h2>
          <p className="text-sm text-gray-600 mt-1">
            {authorizations.filter((a) => a.status === 'Pending').length}{' '}
            pending reviews
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {authorizations.map((auth) => {
            const sla = calculateSLA(auth.requested_date, auth.sla_hours);
            const isSelected = selectedAuth?.id === auth.id;

            return (
              <div
                key={auth.id}
                onClick={() => {
                  setSelectedAuth(auth);
                  setDecisionRationale(auth.decision_rationale || '');
                }}
                className={`p-4 border-b border-gray-200 cursor-pointer transition-colors ${
                  isSelected ? 'bg-purple-50 border-l-4 border-l-purple-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {auth.auth_number}
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      {auth.member
                        ? `${auth.member.first_name} ${auth.member.last_name}`
                        : 'Unknown Member'}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                      auth.priority
                    )}`}
                  >
                    {auth.priority}
                  </span>
                </div>

                <p className="text-sm text-gray-900 font-medium mb-2">
                  {auth.service_type}
                </p>

                <div className="flex items-center justify-between">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                      auth.status
                    )}`}
                  >
                    {auth.status}
                  </span>
                  {auth.status === 'Pending' && (
                    <div className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      <span className={`text-xs font-medium ${sla.color}`}>
                        {sla.text}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Panel - Authorization Details */}
      {selectedAuth ? (
        <div className="flex-1 space-y-6 overflow-y-auto">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Authorization Review
            </h1>
            <p className="text-gray-600 mt-1">{selectedAuth.auth_number}</p>
          </div>

          {/* Member Information */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 text-purple-600 mr-2" />
              Member Information
            </h2>
            {selectedAuth.member && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedAuth.member.first_name}{' '}
                    {selectedAuth.member.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Member ID</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedAuth.member.member_id}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">DOB</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatDate(selectedAuth.member.date_of_birth)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Risk Score</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedAuth.member.risk_score} -{' '}
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        selectedAuth.member.risk_band === 'High' ||
                        selectedAuth.member.risk_band === 'Critical'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {selectedAuth.member.risk_band}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Service Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileCheck className="w-5 h-5 text-purple-600 mr-2" />
              Service Details
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Service Type</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedAuth.service_type}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Priority</p>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-medium rounded-full border ${getPriorityColor(
                      selectedAuth.priority
                    )} mt-1`}
                  >
                    {selectedAuth.priority}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Requested Date</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {formatDate(selectedAuth.requested_date)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">SLA Deadline</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {calculateSLA(
                      selectedAuth.requested_date,
                      selectedAuth.sla_hours
                    ).text}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Assigned To</p>
                  <p className="font-medium text-gray-900 mt-1">
                    {selectedAuth.assigned_to || 'Unassigned'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <span
                    className={`inline-block px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                      selectedAuth.status
                    )} mt-1`}
                  >
                    {selectedAuth.status}
                  </span>
                </div>
              </div>

              {selectedAuth.expiration_date && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-500">Expiration Date:</span>
                    <span className="font-medium text-gray-900 ml-2">
                      {formatDate(selectedAuth.expiration_date)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Summary */}
          {selectedAuth.ai_summary && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-6">
              <div className="flex items-start">
                <Sparkles className="w-6 h-6 text-purple-600 mr-3 mt-1" />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    AI-Extracted Summary
                  </h2>
                  <p className="text-sm text-gray-700">
                    {selectedAuth.ai_summary}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Missing Information Checklist */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 text-amber-600 mr-2" />
              Missing Information
            </h2>
            {selectedAuth.missing_info.length > 0 ? (
              <div className="space-y-2">
                {selectedAuth.missing_info.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label className="text-sm text-gray-700">{info}</label>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-green-600 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                All required information is present
              </p>
            )}
          </div>

          {/* Decision Rationale */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Decision Rationale
            </h2>
            <textarea
              value={decisionRationale}
              onChange={(e) => setDecisionRationale(e.target.value)}
              placeholder="Enter your decision rationale and clinical justification..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              rows={6}
            />
            <div className="flex items-center justify-between mt-4">
              <button className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300 flex items-center">
                <Sparkles className="w-5 h-5 mr-2" />
                AI Draft Rationale
              </button>
              <span className="text-sm text-gray-600">
                {decisionRationale.length} characters
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {selectedAuth.status === 'Pending' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-end space-x-4">
                <button className="px-6 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300">
                  Request More Information
                </button>
                <button className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center">
                  <XCircle className="w-5 h-5 mr-2" />
                  Deny
                </button>
                <button className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Approve
                </button>
              </div>
            </div>
          )}

          {selectedAuth.status !== 'Pending' && (
            <div
              className={`rounded-lg border p-6 ${
                selectedAuth.status === 'Approved'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center">
                {selectedAuth.status === 'Approved' ? (
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600 mr-3" />
                )}
                <div>
                  <p
                    className={`font-semibold ${
                      selectedAuth.status === 'Approved'
                        ? 'text-green-900'
                        : 'text-red-900'
                    }`}
                  >
                    Authorization {selectedAuth.status}
                  </p>
                  {selectedAuth.decision_date && (
                    <p
                      className={`text-sm mt-1 ${
                        selectedAuth.status === 'Approved'
                          ? 'text-green-700'
                          : 'text-red-700'
                      }`}
                    >
                      Decision Date: {formatDate(selectedAuth.decision_date)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FileCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select an authorization to review</p>
          </div>
        </div>
      )}
    </div>
  );
}
